import React from "react";
import moment from "moment";
import { Box } from "@mui/material";
import { MyHrMonthSelector } from "my-hr/components";
import { AllLeavesCardLeaveFilterCollapse } from "my-leave/components";
import {
  allLeaveRequests,
  allLeaveRequestsIsMobile,
  examinerDashboardSummary,
  examinerDashboardSummaryIsMobile,
} from "my-leave/configs/table-defs";
import { OcCard, OcDataTable } from "oc/components";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

type MyLeavesAllLeavesCardProps = {
  currentDate: Date;
  data: any;
  filterable: boolean;
  loading: boolean;
  onAddFilter: (...args: any[]) => void;
  onCurrentDateChange: (date: Date) => void;
  onFilterableChange: (...args: any[]) => void;
  onlyRequested: boolean;
  onOnlyRequestedChange: (...args: any[]) => void;
  onRemoveFilter: (...args: any[]) => void;
  onSelectRow: (...args: any[]) => void;
  onShowRemoteWorksChange: (...args: any[]) => void;
  onTableDataChange: (...args: any[]) => void;
  product: any;
  role: string;
  roleFilters: any;
  showRemoteWorks: boolean;
  tableData: any;
  tableId: string;
  onLoadQuery: (...args: any[]) => void;
  onClearFilters: (...args: any[]) => void;
  buttonDisable: boolean;
  contract: any;
  label: string;
  selectedRowId: string | number | undefined;
  onRowSelection: (...args: any[]) => void;
  examinerDasboard: boolean;
  handleMoreButton: (...args: any[]) => void;
};

export const MyLeavesAllLeavesCard = ({
  currentDate,
  data,
  filterable,
  loading,
  onAddFilter,
  onCurrentDateChange,
  onFilterableChange,
  onOnlyRequestedChange,
  onRemoveFilter,
  onSelectRow,
  onShowRemoteWorksChange,
  onTableDataChange,
  product,
  role,
  roleFilters,
  showRemoteWorks,
  tableData,
  tableId,
  onLoadQuery,
  onClearFilters,
  buttonDisable,
  contract,
  label,
  selectedRowId,
  onRowSelection,
  examinerDasboard,
  handleMoreButton,
  onlyRequested,
}: MyLeavesAllLeavesCardProps) => {
  const isMobile = useOcIsMobile();

  const handleLoadQuery = React.useCallback((...args: any[]) => {
    if (!onlyRequested && tableData?.filterField === 'leaveBadges') {
      onTableDataChange({ filterField: undefined, filterSelected: [], filterShowForm: false });
    }
    onLoadQuery(...args);
  }, [onlyRequested, tableData?.filterField, onTableDataChange, onLoadQuery]);

  const handlePrev = () => {
    onCurrentDateChange(moment(currentDate).subtract(1, "month").toDate());
  };

  const handleNext = () => {
    onCurrentDateChange(moment(currentDate).add(1, "month").toDate());
  };

  let monthLabel = moment(currentDate).format("MMMM");
  let yearLabel = moment(currentDate).format("YYYY");

  let tableDef;
  if (examinerDasboard) {
    if (isMobile) {
      tableDef = examinerDashboardSummaryIsMobile;
    } else {
      tableDef = examinerDashboardSummary;
    }
  } else {
    if (isMobile) {
      tableDef = allLeaveRequestsIsMobile;
    } else {
      tableDef = allLeaveRequests;
    }
  }

  if (!onlyRequested && tableDef?.columns) {
    tableDef = {
      ...tableDef,
      columns: tableDef.columns.map((col: any) =>
        col.id === 'leaveBadges' ? { ...col, filter: false } : col
      ),
    };
  }

  const filteredTableDef = {
    id: allLeaveRequests?.id,
    columns: allLeaveRequests?.columns?.filter(
      (column) =>
        !allLeaveRequestsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  // Filter data rows based on tableData.filterSelected
  const filteredData = React.useMemo(() => {
    if (!tableData?.filterField || !tableData?.filterSelected || tableData?.filterSelected?.length === 0) {
      return data;
    }

    if (tableData.filterField === "leaveBadges") {
      const filteredRows = data?.rows?.filter((row: any) => {
        return tableData.filterSelected.some((selected: string) => {
          if (selected === "past") return (row.pastRequestedLeavesCount || 0) > 0;
          if (selected === "current") return (row.currentRequestedLeavesCount || 0) > 0;
          if (selected === "future") return (row.futureRequestedLeavesCount || 0) > 0;
          return false;
        });
      });
      return { ...data, rows: filteredRows, total: filteredRows?.length || 0 };
    }

    const filteredRows = data?.rows?.filter((row: any) => {
      const cellValue = row[tableData.filterField];

      // If the row has "both" badge, show it for any selected filter
      if (cellValue === "both") {
        return true;
      }

      // Otherwise, only show if the cell value matches one of the selected filters
      return tableData.filterSelected.includes(cellValue);
    });
    return { ...data, rows: filteredRows, total: filteredRows?.length || 0 };
  }, [data, tableData?.filterField, tableData?.filterSelected]);

  return (
    <OcCard label={label}>
      <>
        <Box height="16px" />
        {role === "viewer" && !examinerDasboard && (
          <>
            <MyHrMonthSelector
              year={yearLabel}
              month={monthLabel}
              backChevron={false}
              previousonClick={handlePrev}
              nextOnClick={handleNext}
              isMobile={isMobile}
              onClose={() => { }}
            />
            <Box height="32px" />
          </>
        )}
        <AllLeavesCardLeaveFilterCollapse
          buttonDisable={buttonDisable}
          contract={contract}
          currentDate={currentDate}
          data={data}
          examinerDasboard={examinerDasboard}
          filterable={filterable}
          monthFiltering={false}
          onAddFilter={onAddFilter}
          onClearFilters={onClearFilters}
          onCurrentDateChange={onCurrentDateChange}
          onFilterableChange={onFilterableChange}
          onLoadQuery={handleLoadQuery}
          onRemoveFilter={onRemoveFilter}
          onShowRemoteWorksChange={onShowRemoteWorksChange}
          product={product}
          role={role}
          roleFilters={roleFilters}
          setOnlyRequested={onOnlyRequestedChange}
          showRemoteWorks={showRemoteWorks}
          loading={loading && !data.ros}
        />
        <Box paddingTop="32px">
          <OcDataTable
            data={filteredData}
            loading={loading && !data.rows}
            tableDef={tableDef}
            tableData={tableData}
            setTableData={onTableDataChange}
            onSelectRow={onSelectRow}
            tableId={tableId}
            pagination={true}
            selectedRowId={selectedRowId}
            onRowSelection={onRowSelection}
            examinerDasboard={examinerDasboard}
            role={role}
            filteredTableDef={
              examinerDasboard ? examinerDashboardSummary : filteredTableDef
            }
            handleMoreButton={handleMoreButton}
          />
        </Box>
      </>
    </OcCard>
  );
};

export default MyLeavesAllLeavesCard;
